/**
 * LiveOpsModule — the Live Ops tab (P12).
 *
 * Surfaces the live-ops layer: active Chronicler event banners (Creator Boom /
 * Diplomatic Summit / Winter Blues), the Season countdown, the Commons Council
 * (the open election with INF-weighted voting, the seats, and a council-action
 * console for seated members), and Legacy-world plaques. The client only RENDERS
 * replicated rows + CALLS reducers — every aggregate, tally, eligibility check,
 * and consequence is server-authoritative.
 */

import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import type { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { iconMarkup } from '../lib/icons.js';

const DAY_MICROS = 86_400_000_000;
const CHRONICLER_LABEL: Record<string, string> = {
  creator_boom: 'Creator Boom — uploads cheaper + payouts boosted (48h)',
  diplomatic_summit: 'Diplomatic Summit — broker a reunification for INF',
  winter_blues: 'Winter Blues — free venues spawned to reinvigorate activity',
};

export class LiveOpsModule implements IModule {
  readonly name = 'LiveOps';
  private bound = false;
  private els: Record<string, HTMLElement> = {};

  init(_app: App): void {
    for (const id of ['lo-chronicler', 'lo-season', 'lo-council', 'lo-legacy', 'lo-note']) {
      const el = document.getElementById(id);
      if (el) this.els[id] = el;
    }
    bus.on('atlas:account-ready', () => { this.bind(); this.render(); });
    bus.on('atlas:liveops-updated', () => this.render());
    bus.on('atlas:reputation-updated', () => this.render());
    this.render();
  }

  private bind(): void {
    if (this.bound) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.bound = true;
    const ping = () => bus.emit('atlas:liveops-updated', {});
    const tables = [conn.db.season, conn.db.chroniclerEvent, conn.db.councilTerm, conn.db.councilVote, conn.db.councilSeat, conn.db.councilAction, conn.db.legacyGrant];
    for (const tbl of tables) {
      const t = tbl as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping); t.onUpdate(ping); t.onDelete(ping);
    }
  }

  private render(): void {
    const conn = atlas.conn;
    if (!conn || !conn.identity) return;
    this.renderChronicler(conn);
    this.renderSeason(conn);
    this.renderCouncil(conn, conn.identity);
    this.renderLegacy(conn);
  }

  // Returns a bigint to preserve precision in bigint timestamp arithmetic — a
  // JS number loses the low bits for far-future micros-since-epoch values.
  private nowMicros(): bigint { return BigInt(Date.now()) * 1000n; }

  private renderChronicler(conn: DbConnection): void {
    const el = this.els['lo-chronicler'];
    if (!el) return;
    const now = this.nowMicros();
    const active = [...conn.db.chroniclerEvent.iter()].filter((e) => e.active && e.endsAt.microsSinceUnixEpoch > now);
    if (active.length === 0) { el.innerHTML = '<div class="empty">No live Chronicler events. The world is calm.</div>'; return; }
    el.innerHTML = active.map((e) => {
      const left = Math.max(0, Math.round(Number(e.endsAt.microsSinceUnixEpoch - now) / DAY_MICROS));
      const summit = e.kind === 'diplomatic_summit'
        ? `<div class="actions"><button data-broker class="action secondary">Broker a reunification</button></div>` : '';
      return `<div class="market-item"><div class="head"><span class="kind">${CHRONICLER_LABEL[e.kind] ?? this.esc(e.kind)}</span><span class="price">~${left}d left</span></div>${summit}</div>`;
    }).join('');
    el.querySelector('[data-broker]')?.addEventListener('click', () => this.broker());
  }

  private renderSeason(conn: DbConnection): void {
    const el = this.els['lo-season'];
    if (!el) return;
    let active = null;
    for (const s of conn.db.season.iter()) if (s.active) active = s;
    if (!active) { el.innerHTML = '<div class="empty">No season has opened yet.</div>'; return; }
    const left = Math.max(0, Math.round(Number(active.endsAt.microsSinceUnixEpoch - this.nowMicros()) / DAY_MICROS));
    el.innerHTML = `<div class="market-item"><div class="head"><span class="kind">Season ${active.seasonIndex}</span><span class="price">~${left}d left</span></div><div class="meta">Persistent worlds, reputations, charters, and the Memory Book carry over; Trending resets.</div></div>`;
  }

  private renderCouncil(conn: DbConnection, viewer: Identity): void {
    const el = this.els['lo-council'];
    if (!el) return;
    const open = [...conn.db.councilTerm.iter()].filter((t) => !t.closed).sort((a, b) => (a.termId > b.termId ? -1 : 1))[0];
    let latestSeated = 0n;
    for (const s of conn.db.councilSeat.iter()) if (s.termId > latestSeated) latestSeated = s.termId;
    const seats = [...conn.db.councilSeat.iter()].filter((s) => s.termId === latestSeated);
    const isSeated = seats.some((s) => s.holder.isEqual(viewer));
    const names = new Map<string, string>();
    for (const a of conn.db.account.iter()) names.set(a.identity.toHexString(), a.displayName || a.username);

    let html = '';
    if (open) {
      // Tally current votes for the open term.
      const tally = new Map<string, bigint>();
      for (const v of conn.db.councilVote.iter()) if (v.termId === open.termId) {
        const hex = v.candidate.toHexString();
        tally.set(hex, (tally.get(hex) ?? 0n) + v.infSpent);
      }
      const opts = [...conn.db.account.iter()].filter((a) => !a.identity.isEqual(viewer))
        .map((a) => `<option value="${a.identity.toHexString()}">${this.esc(a.displayName || a.username)}</option>`).join('');
      html += `<h4>Election — term ${open.termId} (${open.seats} seats)</h4>`;
      html += `<div class="row"><select id="lo-vote-cand">${opts || '<option value="">(no candidates)</option>'}</select><input type="number" id="lo-vote-inf" min="1" placeholder="INF" style="width:80px" /><button class="action" id="lo-vote-btn">Vote</button></div>`;
      html += `<div class="note" id="lo-vote-note"></div>`;
      const tallyRows = [...tally.entries()].sort((a, b) => (a[1] > b[1] ? -1 : 1))
        .map(([hex, inf]) => `<div class="meta">${this.esc(names.get(hex) ?? hex.slice(0, 10) + '…')} — ${inf} INF</div>`).join('');
      html += tallyRows || '<div class="empty">No votes yet.</div>';
    } else if (seats.length > 0) {
      html += `<h4>Seated Council (term ${latestSeated})</h4>`;
      html += seats.sort((a, b) => (a.infTotal > b.infTotal ? -1 : 1))
        .map((s) => `<div class="meta">${this.esc(names.get(s.holder.toHexString()) ?? s.holder.toHexString().slice(0, 10) + '…')} — ${s.infTotal} INF${s.holder.isEqual(viewer) ? ' (you)' : ''}</div>`).join('');
      if (isSeated) {
        html += `<h4>Council action console</h4>`;
        html += `<div class="row"><select id="lo-act-kind"><option value="event_boost">Boost event</option><option value="event_suppress">Suppress event</option><option value="market_curate">Curate marketplace</option><option value="dispute_resolve">Resolve dispute</option></select><input type="number" id="lo-act-target" min="0" placeholder="target id" style="width:90px" /></div>`;
        html += `<div class="row"><input type="text" id="lo-act-note" maxlength="256" placeholder="note" /><button class="action" id="lo-act-btn">Act</button></div>`;
        html += `<div class="note" id="lo-act-note-status"></div>`;
      }
    } else {
      html = '<div class="empty">No Council election is open.</div>';
    }
    el.innerHTML = html;
    el.querySelector('#lo-vote-btn')?.addEventListener('click', () => open && this.vote(open.termId));
    el.querySelector('#lo-act-btn')?.addEventListener('click', () => this.act());
  }

  private renderLegacy(conn: DbConnection): void {
    const el = this.els['lo-legacy'];
    if (!el) return;
    const grants = [...conn.db.legacyGrant.iter()];
    if (grants.length === 0) { el.innerHTML = '<div class="empty">No Legacy worlds yet — earned by 100+ visitors over 6+ months.</div>'; return; }
    el.innerHTML = grants.map((g) => {
      const w = conn.db.world.worldId.find(g.worldId);
      return `<div class="market-item"><div class="head"><span class="kind">${iconMarkup('bank')}${this.esc(w ? w.name : `world #${g.worldId}`)}</span><span class="price">${g.subsidyPct}% subsidy</span></div><div class="meta">Legacy — platform-delete-protected (only the owner can remove it).</div></div>`;
    }).join('');
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────

  private vote(termId: bigint): void {
    const conn = atlas.conn;
    const candHex = (document.getElementById('lo-vote-cand') as HTMLSelectElement | null)?.value;
    const inf = BigInt(Math.max(0, Math.floor(Number((document.getElementById('lo-vote-inf') as HTMLInputElement | null)?.value ?? 0))));
    if (!conn || !candHex || inf <= 0n) return this.note('lo-vote-note', 'Pick a candidate and a positive INF amount.', 'err');
    const cand = [...conn.db.account.iter()].find((a) => a.identity.toHexString() === candHex)?.identity;
    if (!cand) return;
    this.note('lo-vote-note', 'Voting…', '');
    void conn.reducers.castCouncilVote({ termId, candidate: cand, inf })
      .then(() => this.note('lo-vote-note', `Voted (${inf} INF burned).`, 'ok'))
      .catch((e: Error) => this.note('lo-vote-note', e.message, 'err'));
  }

  private act(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const kind = (document.getElementById('lo-act-kind') as HTMLSelectElement | null)?.value ?? 'event_boost';
    const target = BigInt(Math.max(0, Math.floor(Number((document.getElementById('lo-act-target') as HTMLInputElement | null)?.value ?? 0))));
    const note = (document.getElementById('lo-act-note') as HTMLInputElement | null)?.value ?? '';
    void conn.reducers.councilAct({ kind, target, note })
      .then(() => this.note('lo-act-note-status', 'Action recorded.', 'ok'))
      .catch((e: Error) => this.note('lo-act-note-status', e.message, 'err'));
  }

  private broker(): void {
    const conn = atlas.conn;
    if (!conn) return;
    // Minimal UX: broker a placeholder pair (a real UI would pick two groups).
    void conn.reducers.brokerReunification({ groupA: 1n, groupB: 2n })
      .then(() => this.note('lo-note', 'Reunification brokered — INF awarded.', 'ok'))
      .catch((e: Error) => this.note('lo-note', e.message, 'err'));
  }

  private note(id: string, text: string, cls: '' | 'ok' | 'err'): void {
    const el = document.getElementById(id) ?? this.els[id];
    if (!el) return;
    el.className = cls ? `note ${cls}` : 'note';
    el.textContent = text;
  }

  /** Escape user-controlled strings before interpolating into innerHTML (anti-XSS). */
  private esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'));
  }
}
